import {ToothTreatment, InvoiceResponse} from "../api-client";

// Invoice status constants (should match backend enum)
export const INVOICE_STATUS = {
    PAID: 'PAID',
    UNPAID: 'UNPAID',
    UNPAID_PROFORMA: 'UNPAID_PROFORMA',  // Proforma invoices use this status
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    CANCELLED: 'CANCELLED'
} as const;

export const calculatePayments = (toothTreatments: ToothTreatment[]) => {
    let total = 0;
    let discount = 0;
    let laboratory = 0;
    let paid = 0;
    let due = 0;

    for (const toothTreatment of toothTreatments) {
        let proceduresPrice = 0;
        let proceduresDiscounts = 0;
        let proceduresLaboratoryPrice = 0;

        for (const procedure of toothTreatment.procedures || []) {
            proceduresPrice += procedure.price! - procedure.price! * (procedure.discount! / 100) + procedure.laboratoryPrice!;
            proceduresLaboratoryPrice += procedure.laboratoryPrice!;
            proceduresDiscounts += procedure.price! - procedure.price! * (procedure.discount! / 100);

            if (procedure.paidAt) {
                paid += procedure.price! - procedure.price! * (procedure.discount! / 100) +
                    procedure.laboratoryPrice!;
            } else {
                due += procedure.price! - procedure.price! * (procedure.discount! / 100) +
                    procedure.laboratoryPrice!;
            }
        }

        total += toothTreatment.price! - toothTreatment.price! * (toothTreatment.discount! / 100) + toothTreatment.laboratoryPrice! + proceduresPrice;

        const toothTreatmentPriceWithDiscount =
            toothTreatment.price! - toothTreatment.price! * (toothTreatment.discount! / 100);
        discount += toothTreatmentPriceWithDiscount + proceduresDiscounts;
        laboratory += toothTreatment.laboratoryPrice! + proceduresLaboratoryPrice;

        if (toothTreatment.paidAt) {
            paid += toothTreatment.price! - toothTreatment.price! * (toothTreatment.discount! / 100) +
                toothTreatment.laboratoryPrice!;
        } else {
            due += toothTreatment.price! - toothTreatment.price! * (toothTreatment.discount! / 100) +
                toothTreatment.laboratoryPrice!;
        }
    }

    return {
        total,
        laboratory,
        discount,
        paid,
        due
    };
}

/**
 * Calculate financial summary from invoices and proforma invoices
 * This is the correct calculation method as invoices are the source of truth for payments
 *
 * @param invoices - Array of regular invoices
 * @param proformaInvoices - Array of proforma invoices
 * @returns Financial summary with total, paid, and due amounts
 */
export const calculateFinancialSummary = (
    invoices: InvoiceResponse[],
    proformaInvoices: InvoiceResponse[] = []
) => {
    // Combine both invoice types
    const allInvoices = [...invoices, ...proformaInvoices];

    // Filter out cancelled invoices
    const activeInvoices = allInvoices.filter(
        invoice => invoice.status !== INVOICE_STATUS.CANCELLED
    );

    // Calculate totals with proper rounding to avoid floating-point precision issues
    const totalValue = Math.round(activeInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0) * 100) / 100;

    const paidInvoices = activeInvoices.filter(
        invoice => invoice.status === INVOICE_STATUS.PAID
    );
    const totalPaid = Math.round(paidInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0) * 100) / 100;

    const unpaidInvoices = activeInvoices.filter(
        invoice => invoice.status === INVOICE_STATUS.UNPAID ||
                   invoice.status === INVOICE_STATUS.UNPAID_PROFORMA ||  // Include unpaid proformas
                   invoice.status === INVOICE_STATUS.PARTIALLY_PAID
    );
    const totalDue = Math.round(unpaidInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0) * 100) / 100;

    return {
        total: totalValue,
        paid: totalPaid,
        due: totalDue
    };
}